import React, { useEffect, useState } from 'react';
import {
  MdExplicit,
  MdLocationOn,
  MdLock,
  MdOpenInNew,
  MdStorage,
  MdTimeline,
  MdWarning
} from 'react-icons/md';
import { useTheme } from '../../contexts/ThemeContext';

import { Page } from 'components';
import { Badge, Button, Col, Container, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { HorizontalBar } from 'react-chartjs-2';

import Header from 'components/Header/Header';
import Reload from '../../components/Reload/Reload';
import ApiGServer from 'services/ApiGServer';
import ApiMServer from 'services/ApiMServer';
import MeasurementsDashboard from './components/MeasurementsDashboard';
import UnlocksDashboard from './components/UnlocksDashboard';
import UsersDashboard from './components/UsersDashboard';
import {
  KPI,
  chartColors,
  getChartTextColor,
  getDashboardCardSurface,
  getDashboardShellStyles,
  getDashboardTabsStyle
} from 'constants/dashboardColors';
import './PainelExecutivo.css';

const PAGE_TO_URL_MAP = {
  executivePanel: '/painel-executivo',
  users: '/users',
  cooperatives: '/cooperatives',
  units: '/units',
  equipments: '/equipments',
  nir: '/nir',
  equipmentsreplace: '/equipments-replace',
  updates: '/updates',
  models3d: '/models-3d',
  logs: '/logs',
  maps: '/maps',
  mapsunits: '/maps?tab=units',
  support: '/support',
  supportnir: '/supportnir',
  supportdry: '/supportdry',
  supportOrange: '/remote-access',
  supportiot: '/supportiot',
  equipmentsQL: '/equipmentsQL',
  notifications: '/notifications',
  userspassword: '/userspassword',
  general: '/settings/general'
};

const EQUIPMENT_METRICS_CACHE_KEY = 'metrics:equipment-dashboard';
const EQUIPMENT_METRICS_CACHE_TTL_MS = 5 * 60 * 1000;

const EMPTY_DASHBOARD_METRICS = {
  generatedAt: null,
  totals: {
    equipments: 0,
    withCompany: 0,
    withoutCompany: 0,
    withLocation: 0,
    withoutLocation: 0
  },
  distributions: {
    byType: {},
    byMode: {},
    byPlan: {},
    bySoftwareVersion: {},
    byFirmwareVersion: {},
    byState: {},
    byCity: {}
  },
  health: {
    withoutSoftwareVersion: 0,
    withoutCalibrationDate: 0,
    certified: 0,
    isentKey: 0,
    pendingReplace: 0
  },
  activation: {
    byRawValue: {},
    byDays: {},
    missing: 0,
    invalid: 0
  },
  entries: {
    lastDay: 0,
    lastWeek: 0,
    lastMonth: 0,
    byDay: {},
    byMonth: {},
    missingCreatedAt: 0
  }
};

const getChartOptions = (isDarkTheme) => ({
  scales: {
    yAxes: [{
      ticks: {
        beginAtZero: true,
        fontColor: getChartTextColor(isDarkTheme),
        fontStyle: 'bold',
        precision: 0
      },
      gridLines: {
        display: false
      },
      maxBarThickness: 30
    }],
    xAxes: [{
      ticks: {
        beginAtZero: true,
        fontColor: getChartTextColor(isDarkTheme),
        padding: 10,
        precision: 0
      },
      gridLines: {
        color: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        zeroLineColor: isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    }]
  },
  legend: {
    display: false
  },
  maintainAspectRatio: false,
  responsive: true,
  tooltips: {
    backgroundColor: isDarkTheme ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
    titleFontColor: isDarkTheme ? '#f8f9fa' : '#333',
    bodyFontColor: isDarkTheme ? '#adb5bd' : '#666',
    borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    borderWidth: 1,
    displayColors: true,
    cornerRadius: 6,
    caretSize: 6,
    titleFontStyle: 'bold',
    callbacks: {
      label: (item) => `Quantidade: ${item.value}`
    }
  }
});

const createBarGradient = (ctx, colors, isDark) => {
  const gradient = ctx.createLinearGradient(0, 0, 300, 0);
  gradient.addColorStop(0, isDark ? colors.dark : colors.light);
  gradient.addColorStop(1, isDark ? `${colors.dark}99` : `${colors.light}99`);
  return gradient;
};

const compareVersions = (v1 = '', v2 = '') => {
  const parts1 = v1.split('.').map((part) => Number(part));
  const parts2 = v2.split('.').map((part) => Number(part));

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const num1 = Number.isNaN(parts1[i]) ? 0 : parts1[i] || 0;
    const num2 = Number.isNaN(parts2[i]) ? 0 : parts2[i] || 0;

    if (num1 > num2) return -1;
    if (num1 < num2) return 1;
  }

  return v1.localeCompare(v2);
};

const normalizeEquipmentMetrics = (data = {}) => ({
  generatedAt: data.generatedAt || null,
  totals: {
    ...EMPTY_DASHBOARD_METRICS.totals,
    ...(data.totals || {})
  },
  distributions: {
    ...EMPTY_DASHBOARD_METRICS.distributions,
    ...(data.distributions || {})
  },
  health: {
    ...EMPTY_DASHBOARD_METRICS.health,
    ...(data.health || {})
  },
  activation: {
    ...EMPTY_DASHBOARD_METRICS.activation,
    ...(data.activation || {})
  },
  entries: {
    ...EMPTY_DASHBOARD_METRICS.entries,
    ...(data.entries || {})
  }
});

const readEquipmentMetricsCache = () => {
  try {
    const raw = localStorage.getItem(EQUIPMENT_METRICS_CACHE_KEY);
    if (!raw) return null;

    const { cachedAt, data } = JSON.parse(raw);
    if (!cachedAt || !data) return null;
    if (Date.now() - cachedAt > EQUIPMENT_METRICS_CACHE_TTL_MS) return null;

    return normalizeEquipmentMetrics(data);
  } catch {
    return null;
  }
};

const writeEquipmentMetricsCache = (data) => {
  localStorage.setItem(
    EQUIPMENT_METRICS_CACHE_KEY,
    JSON.stringify({ cachedAt: Date.now(), data })
  );
};

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR');

const translateMetricLabel = (label) => {
  const translations = {
    OWNER: 'Próprio',
    RENTAL: 'Locação',
    'NÃO INFORMADO': 'Sem plano',
    'NAO INFORMADO': 'Sem plano',
    'Não informado': 'Sem plano',
    'Nao informado': 'Sem plano',
    'não informado': 'Sem plano',
    'nao informado': 'Sem plano'
  };

  return translations[label] || label;
};

const formatGeneratedAt = (value) => {
  if (!value) return 'Sem data de geração';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem data de geração';

  return date.toLocaleString('pt-BR');
};

const getMonthKeyFromDate = (value) => {
  const date = value ? new Date(value) : new Date();
  const validDate = Number.isNaN(date.getTime()) ? new Date() : date;
  const month = String(validDate.getMonth() + 1).padStart(2, '0');

  return `${validDate.getFullYear()}-${month}`;
};

const formatMonthLabel = (monthKey) => {
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);

  if (Number.isNaN(date.getTime())) return 'mês atual';

  return date.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric'
  });
};

const filterEntriesByMonth = (entriesByDay = {}, monthKey) => {
  return Object.entries(entriesByDay || {})
    .filter(([day]) => day.startsWith(monthKey))
    .reduce((acc, [day, value]) => {
      acc[day] = value;
      return acc;
    }, {});
};

const objectToSeries = (source = {}, sortType = 'value') => {
  const entries = Object.entries(source || {});

  if (sortType === 'version') {
    entries.sort((a, b) => compareVersions(a[0], b[0]));
  } else if (sortType === 'date') {
    entries.sort((a, b) => a[0].localeCompare(b[0]));
  } else {
    entries.sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0));
  }

  return {
    labels: entries.map(([label]) => translateMetricLabel(label)),
    data: entries.map(([, value]) => Number(value || 0))
  };
};

const filterMetricSourceByLabels = (source = {}, hiddenLabels = []) => {
  const hiddenLabelSet = hiddenLabels.map((label) => String(label).toLowerCase());

  return Object.entries(source || {}).reduce((acc, [label, value]) => {
    const translatedLabel = translateMetricLabel(label);
    if (!hiddenLabelSet.includes(String(translatedLabel).toLowerCase())) {
      acc[label] = value;
    }

    return acc;
  }, {});
};

const getPercent = (value, total) => {
  if (!total) return 0;
  return Math.round((Number(value || 0) / Number(total || 0)) * 100);
};

const getChartData = (canvas, source, colorKey, darkMode, sortType) => {
  const ctx = canvas.getContext('2d');
  const series = objectToSeries(source, sortType);
  const color = chartColors[colorKey] || chartColors.simt;

  return {
    labels: series.labels,
    datasets: [
      {
        label: 'Equipamentos',
        data: series.data,
        backgroundColor: createBarGradient(ctx, color, darkMode),
        borderColor: darkMode ? color.dark : color.light,
        borderWidth: 1,
        hoverBackgroundColor: darkMode ? color.dark : color.light,
        hoverBorderColor: darkMode ? '#ffffff' : '#000000',
        barPercentage: 0.7,
        categoryPercentage: 0.8
      }
    ]
  };
};

const StatCard = ({ icon, title, value, subtitle, variant = 'default', accent = KPI.secondary, darkMode = false }) => {
  const variantStyles = {
    default: { color: accent },
    warning: { color: KPI.warning },
    danger: { color: KPI.danger },
    success: { color: KPI.primary }
  };
  const currentAccent = variantStyles[variant]?.color || accent;
  const cardSurface = getDashboardCardSurface();

  return (
    <Col xs={12} sm={6} xl={3}>
      <div
        className="h-100"
        style={{
          borderRadius: '18px',
          padding: '18px',
          ...cardSurface,
          borderTop: `4px solid ${currentAccent}`
        }}
      >
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <p className="text-uppercase mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
              {title}
            </p>
            <h2 className="mb-0 fw-bold" style={{ color: 'var(--text-color)' }}>
              {formatNumber(value)}
            </h2>
          </div>
        <div
            className="d-inline-flex align-items-center justify-content-center"
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '14px',
              background: `${currentAccent}22`,
              color: currentAccent
            }}
        >
          {icon}
        </div>
        </div>
        {subtitle && (
          <div className="d-flex align-items-center justify-content-between">
            <small style={{ color: 'var(--text-muted)' }}>{subtitle}</small>
            <span style={{ width: '52px', height: '6px', borderRadius: '999px', background: `${currentAccent}55` }} />
          </div>
        )}
      </div>
    </Col>
  );
};

const getOptionsWithBarValues = (options, darkMode) => ({
  ...options,
  animation: {
    ...(options.animation || {}),
    onComplete: function () {
      const chart = this.chart;
      const ctx = chart.ctx;

      ctx.save();
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      chart.config.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.controller.getDatasetMeta(datasetIndex);
        const drawnLabels = [];
        const chartRight = chart.chartArea?.right || chart.width;
        const chartLeft = chart.chartArea?.left || 0;

        meta.data.forEach((bar, index) => {
          const value = formatNumber(dataset.data[index]);
          const model = bar._model;
          const barWidth = Math.abs(model.x - model.base);
          const textWidth = ctx.measureText(value).width;
          const fitsInside = barWidth > textWidth + 18;
          const x = fitsInside
            ? Math.min(Math.max(model.x - 10, chartLeft + textWidth + 4), chartRight - 4)
            : Math.min(model.x + textWidth + 10, chartRight - 4);
          const labelBox = {
            x1: x - textWidth - 4,
            x2: x + 4,
            y1: model.y - 7,
            y2: model.y + 7
          };
          const overlaps = drawnLabels.some((box) => (
            labelBox.x1 < box.x2 &&
            labelBox.x2 > box.x1 &&
            labelBox.y1 < box.y2 &&
            labelBox.y2 > box.y1
          ));

          if (overlaps) return;

          ctx.fillStyle = fitsInside ? '#ffffff' : darkMode ? '#f8f9fa' : '#1c4220';
          ctx.fillText(value, x, model.y);
          drawnLabels.push(labelBox);
        });
      });

      ctx.restore();
    }
  }
});

const ChartCard = ({ title, badge, source, colorKey, darkMode, options, sortType = 'value', lg = 6, height = 300, showValuesInside = false }) => {
  const hasData = objectToSeries(source, sortType).data.some((value) => value > 0);
  const color = chartColors[colorKey] || chartColors.simt;
  const chartOptions = showValuesInside ? getOptionsWithBarValues(options, darkMode) : options;
  const cardSurface = getDashboardCardSurface();

  return (
    <Col xs={12} lg={lg}>
      <div
        className="h-100"
        style={{
          padding: '18px',
          borderRadius: '18px',
          ...cardSurface,
          borderTop: `4px solid ${darkMode ? color.dark : color.light}`
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="fw-bold mb-0" style={{ color: 'var(--brand-dark-green)' }}>{title}</h5>
            {badge && <Badge className="mt-1 badge-light" pill>{badge}</Badge>}
          </div>
        </div>

        <div style={{ height, position: 'relative' }}>
          {hasData ? (
            <HorizontalBar
              data={(canvas) => getChartData(canvas, source, colorKey, darkMode, sortType)}
              options={chartOptions}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              Sem dados para exibir
            </div>
          )}
        </div>
      </div>
    </Col>
  );
};

const ProgressMetric = ({ label, value, total, accent, darkMode }) => {
  const percent = getPercent(value, total);

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span style={{ color: 'var(--text-color)', fontWeight: 600 }}>{label}</span>
        <span style={{ color: 'var(--text-muted)' }}>{formatNumber(value)} ({percent}%)</span>
      </div>
      <div style={{ height: '9px', background: 'var(--card-header-bg)', borderRadius: '999px', overflow: 'hidden' }}>
        <div style={{ width: `${percent}%`, height: '100%', background: accent, borderRadius: '999px' }} />
      </div>
    </div>
  );
};

const RankingPanel = ({ title, source, darkMode, sortType = 'value', accent = '#8fad15', hiddenLabels = [] }) => {
  const series = objectToSeries(source, sortType);
  const hiddenLabelSet = hiddenLabels.map((label) => label.toLowerCase());
  const items = series.labels
    .map((label, index) => ({
      label,
      value: series.data[index]
    }))
    .filter((item) => !hiddenLabelSet.includes(String(item.label).toLowerCase()))
    .slice(0, 7);
  const maxValue = Math.max(...items.map((item) => item.value), 0);

  const cardSurface = getDashboardCardSurface();

  return (
    <div
      className="h-100"
      style={{
        padding: '18px',
        borderRadius: '18px',
        ...cardSurface
      }}
    >
      <h5 className="fw-bold mb-3" style={{ color: 'var(--brand-dark-green)' }}>{title}</h5>
      {items.length ? items.map((item) => (
        <div key={item.label} className="mb-3">
          <div className="d-flex justify-content-between mb-1">
            <span style={{ color: 'var(--text-color)', fontWeight: 600 }}>{item.label}</span>
            <span style={{ color: 'var(--text-muted)' }}>{formatNumber(item.value)}</span>
          </div>
          <div style={{ height: '8px', background: 'var(--card-header-bg)', borderRadius: '999px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${maxValue ? Math.round((item.value / maxValue) * 100) : 0}%`,
                height: '100%',
                background: accent,
                borderRadius: '999px'
              }}
            />
          </div>
        </div>
      )) : (
        <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '220px' }}>
          Sem dados para exibir
        </div>
      )}
    </div>
  );
};

const Metrics = () => {
  const [user] = useState(() => JSON.parse(localStorage.getItem('userData') || 'null'));
  const [pagePermission, setPagePermission] = useState(0);
  const [loading, setLoading] = useState(true);
  const [equipmentMetrics, setEquipmentMetrics] = useState(EMPTY_DASHBOARD_METRICS);
  const [activeTab, setActiveTab] = useState('equipments');
  const [tabLoading, setTabLoading] = useState(false);
  const { darkMode } = useTheme();
  const [options, setOptions] = useState(getChartOptions(darkMode));

  useEffect(() => {
    setOptions(getChartOptions(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const permissions = user?.permission || {};

    if (permissions?.executivePanel?.read !== true) {
      const keys = Object.keys(permissions);
      const firstAllowedPage = keys.find((page) => permissions[page]?.read);

      if (firstAllowedPage) {
        const redirectUrl = PAGE_TO_URL_MAP[firstAllowedPage] || `/${firstAllowedPage}`;
        window.location.replace(redirectUrl);
        return;
      }

      setPagePermission(1);
      return;
    }

    setPagePermission(2);
  }, [user]);

  useEffect(() => {
    const handleGetUser = async () => {
      const storedUser = localStorage.getItem('userData');
      if (!storedUser) return;

      try {
        const { _id } = JSON.parse(storedUser);
        const { data: newUserData } = await ApiMServer.get(`/users/${_id}`);

        if (newUserData) {
          const updatedUser = {
            ...JSON.parse(storedUser),
            ...newUserData
          };
          localStorage.setItem('userData', JSON.stringify(updatedUser));
        }
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
      }
    };

    const fetchEquipmentDashboard = async () => {
      const cached = readEquipmentMetricsCache();
      if (cached) {
        setEquipmentMetrics(cached);
        setLoading(false);
        return;
      }

      try {
        const { data } = await ApiGServer.get('/equipment/dashboard/metrics');
        const normalized = normalizeEquipmentMetrics(data);

        setEquipmentMetrics(normalized);
        writeEquipmentMetricsCache(normalized);
      } catch (error) {
        setEquipmentMetrics(EMPTY_DASHBOARD_METRICS);
        console.log('Erro ao buscar métricas do dashboard de equipamentos:', error.message);
      } finally {
        setLoading(false);
      }
    };

    handleGetUser();

    if (user?.permission?.executivePanel?.read === true) {
      fetchEquipmentDashboard();
    }
  }, [user]);

  const { totals, distributions, health, activation, entries } = equipmentMetrics;
  const totalWithLocation = Number(totals.withLocation || 0) + Number(totals.withoutLocation || 0);
  const locationPercent = totalWithLocation ? Math.round((Number(totals.withLocation || 0) / totalWithLocation) * 100) : 0;
  const equipmentTotal = Number(totals.equipments || 0);
  const dashboardStyles = getDashboardShellStyles(darkMode);
  const topType = objectToSeries(distributions.byType).labels[0] || '--';
  const currentMonthKey = getMonthKeyFromDate(equipmentMetrics.generatedAt);
  const entriesByCurrentMonthDay = filterEntriesByMonth(entries.byDay, currentMonthKey);
  const currentMonthLabel = formatMonthLabel(currentMonthKey);

  return (
    <>
      {pagePermission === 0 ? (
        <div className="d-flex align-items-center justify-content-center" style={{ height: '100vh' }}>
          <Spinner animation="border" style={{ color: 'var(--brand-light-green)' }} size="lg" />
          <span className="ms-2">Carregando...</span>
        </div>
      ) : pagePermission === 1 ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: '100vh' }}>
          <div className="text-center p-4 dashboard-card" style={{ maxWidth: '500px', borderRadius: '16px' }}>
            <MdLock size={48} style={{ color: 'var(--brand-light-green)', marginBottom: '20px' }} />
            <h2>Acesso Restrito</h2>
            <p className="text-muted">Você não tem permissão para acessar o sistema. Entre em contato com o administrador.</p>
          </div>
        </div>
      ) : pagePermission === 2 ? (
        <Page className="p-4" title="Painel Executivo">
          <Header page="Painel Executivo" />

          {loading && <Reload />}

          <Tabs
            activeKey={activeTab}
            onSelect={(key) => {
              if (key === activeTab) return;
              if (loading || tabLoading) return;
              if (key !== 'equipments') setTabLoading(true);
              setActiveTab(key);
            }}
            className={`mb-3 painel-executivo-tabs ${darkMode ? 'painel-executivo-tabs--pills' : ''}`}
            variant={darkMode ? 'pills' : 'tabs'}
            style={getDashboardTabsStyle(darkMode)}
          >
            <Tab eventKey="equipments" title="Equipamentos" disabled={loading || tabLoading} />
            <Tab eventKey="users" title="Usuários" disabled={loading || tabLoading} />
            <Tab eventKey="activations" title="Ativações" disabled={loading || tabLoading} />
            <Tab eventKey="measurements" title="Medidas" disabled={loading || tabLoading} />
          </Tabs>

          <Container fluid className="px-0">
            <div style={dashboardStyles.shell}>
              {activeTab === 'equipments' ? (
                <>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4" style={{ ...dashboardStyles.panel, padding: '20px' }}>
                    <div>
                      <Badge className="mb-2" style={{ background: KPI.secondary, color: '#fff' }}>
                        Painel Executivo
                      </Badge>
                      <h3 className="fw-bold mb-1" style={dashboardStyles.titleText}>
                        Dashboard de Equipamentos
                      </h3>
                      <p className="mb-0" style={dashboardStyles.mutedText}>
                        Visão geral operacional, distribuição da base instalada e evolução dos cadastros.
                      </p>
                    </div>

                    <div className="d-flex flex-column align-items-start align-items-md-end gap-2">
                      <Badge className="badge-light" pill>
                        Atualizado: {formatGeneratedAt(equipmentMetrics.generatedAt)}
                      </Badge>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          variant="outline-success"
                          className="d-flex align-items-center btn-theme-outline"
                          href="/equipments"
                        >
                          <MdStorage className="me-1" /> Equipamentos
                        </Button>
                        <Button
                          variant="outline-success"
                          className="d-flex align-items-center btn-theme-outline"
                          onClick={() => { window.open(`${process.env.REACT_APP_API_GLOBAL_SERVER}/log`, '_blank') }}
                        >
                          <MdOpenInNew className="me-1" /> Relatório
                        </Button>
                      </div>
                    </div>
              </div>

              <Row className="g-3 mb-4">
                <StatCard
                  icon={<MdExplicit size={28} />}
                  title="Equipamentos"
                  value={totals.equipments}
                  subtitle={`Tipo líder: ${topType}`}
                  accent={KPI.primary}
                  darkMode={darkMode}
                />
                <StatCard
                  icon={<MdLocationOn size={28} />}
                  title="Com Localização"
                  value={totals.withLocation}
                  subtitle={`${locationPercent}% da base localizada`}
                  accent={KPI.secondary}
                  darkMode={darkMode}
                />
                <StatCard
                  icon={<MdWarning size={28} />}
                  title="Sem Localização"
                  value={totals.withoutLocation}
                  subtitle="Exige enriquecimento cadastral"
                  variant="warning"
                  darkMode={darkMode}
                />
                <StatCard
                  icon={<MdTimeline size={28} />}
                  title="Criados no Mês"
                  value={entries.lastMonth}
                  subtitle={`${formatNumber(entries.lastWeek)} na última semana`}
                  accent={KPI.quaternary}
                  darkMode={darkMode}
                />
              </Row>

              <Row className="g-4 mb-4">
                <ChartCard
                  title="Distribuição por Tipo"
                  badge="Tipos com mais de 10 equipamentos"
                  source={distributions.byType}
                  colorKey="simt"
                  darkMode={darkMode}
                  options={options}
                  lg={8}
                  height={360}
                  showValuesInside
                />
                <Col xs={12} lg={4}>
                  <div className="h-100" style={{ ...dashboardStyles.panel, padding: '18px' }}>
                    <h5 className="fw-bold mb-3" style={dashboardStyles.titleText}>Cobertura e Saúde</h5>
                    <ProgressMetric
                      label="Localizados"
                      value={totals.withLocation}
                      total={equipmentTotal}
                      accent={KPI.secondary}
                      darkMode={darkMode}
                    />
                    <ProgressMetric
                      label="Certificados"
                      value={health.certified}
                      total={equipmentTotal}
                      accent={KPI.primary}
                      darkMode={darkMode}
                    />
                    <div className="mt-4 p-3" style={{ borderRadius: '14px', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f4f8f4' }}>
                      <div className="d-flex justify-content-between">
                        <span style={dashboardStyles.mutedText}>Chave isenta</span>
                        <strong style={dashboardStyles.titleText}>{formatNumber(health.isentKey)}</strong>
                      </div>
                      <div className="d-flex justify-content-between mt-2">
                        <span style={dashboardStyles.mutedText}>Ativações inválidas</span>
                        <strong style={dashboardStyles.titleText}>{formatNumber(activation.invalid)}</strong>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-4 mb-4">
                <ChartCard
                  title="Modo de Operação"
                  source={distributions.byMode}
                  colorKey="bimt"
                  darkMode={darkMode}
                  options={options}
                  lg={4}
                  height={260}
                  showValuesInside
                />
                <ChartCard
                  title="Plano"
                  source={distributions.byPlan}
                  colorKey="cpmt"
                  darkMode={darkMode}
                  options={options}
                  lg={4}
                  height={260}
                  showValuesInside
                />
                <Col xs={12} lg={4}>
                  <RankingPanel
                    title="Top Cidades"
                    source={distributions.byCity}
                    darkMode={darkMode}
                    accent={KPI.secondary}
                    hiddenLabels={['Sem plano', 'Não informado', 'Nao informado']}
                  />
                </Col>
              </Row>

              <Row className="g-4 mb-4">
                <ChartCard
                  title="Novos Equipamentos Conectados no Mês"
                  badge={currentMonthLabel}
                  source={entriesByCurrentMonthDay}
                  colorKey="simt"
                  darkMode={darkMode}
                  options={options}
                  sortType="date"
                  lg={6}
                  height={300}
                  showValuesInside
                />
                <ChartCard
                  title="Criações por Mês"
                  badge={`${formatNumber(entries.lastDay)} no último dia | ${formatNumber(entries.missingCreatedAt)} sem data`}
                  source={entries.byMonth}
                  colorKey="bimt"
                  darkMode={darkMode}
                  options={options}
                  sortType="date"
                  lg={6}
                  height={300}
                  showValuesInside
                />
              </Row>

              <Row className="g-4 mb-4">
                <ChartCard
                  title="Versões de Software"
                  source={distributions.bySoftwareVersion}
                  colorKey="simt"
                  darkMode={darkMode}
                  options={options}
                  sortType="version"
                  lg={6}
                  height={300}
                  showValuesInside
                />
                <ChartCard
                  title="Versões de Firmware"
                  source={filterMetricSourceByLabels(distributions.byFirmwareVersion, ['Sem plano'])}
                  colorKey="simt"
                  darkMode={darkMode}
                  options={options}
                  sortType="version"
                  lg={6}
                  height={300}
                  showValuesInside
                />
              </Row>

                </>
              ) : activeTab === 'users' ? (
                <UsersDashboard darkMode={darkMode} onLoadingChange={setTabLoading} />
              ) : activeTab === 'measurements' ? (
                <MeasurementsDashboard darkMode={darkMode} onLoadingChange={setTabLoading} />
              ) : (
                <UnlocksDashboard darkMode={darkMode} onLoadingChange={setTabLoading} />
              )}
            </div>
          </Container>
        </Page>
      ) : null}
    </>
  );
};

export default Metrics;
